import { useTranslation } from 'react-i18next';
/**
 * ContractEditDialog — opens a saved contract for metadata editing,
 * optional-paragraph toggling, signatory selection (for letters), PDF
 * preview/download, and signing.
 *
 * Co-locates AnnexCreateDialog because annexes are only created from a
 * signed master contract via this dialog.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Plus, Lock, Tag, FileSignature } from 'lucide-react';
import { API, STATUS_BADGE, SignatoryStampPreview, filenameFromContentDisposition } from './_shared';
import VariableEditor from './VariableEditor';
import InheritancePanel from './InheritancePanel';
import useAutosave, { AUTOSAVE_LABEL } from '../../hooks/useAutosave';

function AnnexCreateDialog({ masterContract, open, onClose, onSaved, templates, projects }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    template_id: '',
    project_id: '',
    title: '',
    sign_date: new Date().toISOString().slice(0, 10),
    place_of_signing: masterContract.place_of_signing || 'Szczecin',
    valid_from: '',
    valid_to: '',
    variables: {},
  });

  const submit = async () => {
    if (!form.template_id) { toast.error(t('auto.ctr.pickAnAnnexTemplate')); return; }
    if (!form.project_id) { toast.error(t('auto.ctr.annexMustBeLinkedToA')); return; }
    try {
      await axios.post(`${API}/contracts/${masterContract.id}/annexes`, form);
      toast.success(t('auto.ctr.annexDrafted'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Annex create failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="annex-create-dialog">
        <DialogHeader>
          <DialogTitle>New annex to {masterContract.contract_number}</DialogTitle>
          <DialogDescription>
            Employee/customer come from the master contract.
            Annex number = <span className="font-mono">{masterContract.contract_number}/NN</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <InheritancePanel contractId={masterContract.id} templateId={form.template_id || undefined} />
          <div>
            <Label>{t('auto.ctr.annexTemplate')}</Label>
            <Select value={form.template_id} onValueChange={(v) => setForm(prev => ({ ...prev, template_id: v  }))}>
              <SelectTrigger data-testid="annex-template"><SelectValue placeholder={t('auto.ctr.pick')} /></SelectTrigger>
              <SelectContent>
                {templates.length === 0 && <SelectItem value="none" disabled>{t('auto.ctr.noAnnexTemplatesYet')}</SelectItem>}
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('auto.ctr.project')}</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm(prev => ({ ...prev, project_id: v  }))}>
              <SelectTrigger data-testid="annex-project"><SelectValue placeholder={t('auto.ctr.pickAProject')} /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('auto.ctr.signDate')}</Label>
              <Input type="date" value={form.sign_date} onChange={(e) => setForm(prev => ({ ...prev, sign_date: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.place')}</Label>
              <Input value={form.place_of_signing} onChange={(e) => setForm(prev => ({ ...prev, place_of_signing: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.validFrom')}</Label>
              <Input type="date" value={form.valid_from} onChange={(e) => setForm(prev => ({ ...prev, valid_from: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.validTo')}</Label>
              <Input type="date" value={form.valid_to} onChange={(e) => setForm(prev => ({ ...prev, valid_to: e.target.value  }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
          <Button onClick={submit} data-testid="annex-create-submit">{t('auto.ctr.createAnnex')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ContractEditDialog({ contractId, open, onClose, onSaved, employees, customers, projects }) {
  const { t } = useTranslation();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  // Original contract number as loaded from the server — used by the
  // duplicate check so we can tell whether the user actually changed
  // the value (comparing against c.contract_number would always match
  // because onChange updates state before onBlur fires).
  const originalNumberRef = React.useRef(null);
  // Duplicate-number warning state — populated by an on-blur lookup
  // against /contracts/number-in-use. Non-blocking (soft warning only).
  const [numberInUse, setNumberInUse] = useState(null);
  const checkNumberInUse = async (number) => {
    const trimmed = (number || '').trim();
    if (!trimmed || trimmed === (originalNumberRef.current || '').trim()) {
      setNumberInUse(null);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/contracts/number-in-use`, {
        params: { contract_number: trimmed, exclude_contract_id: contractId },
      });
      setNumberInUse(data?.in_use ? data.matches : null);
    } catch (_) { setNumberInUse(null); }
  };

  const [annexOpen, setAnnexOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        axios.get(`${API}/contracts/${contractId}`),
        axios.get(`${API}/contract-templates?active_only=true`),
      ]);
      setC({
        ...r.data,
        // Legacy contracts carry a single `signatory_id` — surface it as
        // party A's signatory so the new pickers show it.
        signatory_a_id: r.data?.signatory_a_id || r.data?.signatory_id || null,
        signatory_b_id: r.data?.signatory_b_id || null,
      });
      // Snapshot the loaded number so the duplicate-check knows when
      // the user has actually diverged from it.
      originalNumberRef.current = r.data?.contract_number || '';
      setTemplates(t.data || []);
    } catch (e) { toast.error(t('auto.ctr.failedToLoadContract')); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) reload(); }, [open, contractId]); // eslint-disable-line

  // Load signatories: this customer's + internal Expertfix (both can sign)
  useEffect(() => {
    const reqs = [axios.get(`${API}/client-stamp-signatories?scope=internal`)];
    if (c?.customer_id) {
      reqs.push(axios.get(`${API}/client-stamp-signatories?customer_id=${c.customer_id}`));
    }
    Promise.all(reqs)
      .then((rs) => {
        const merged = [];
        const seen = new Set();
        for (const r of rs) {
          for (const s of (r.data || [])) {
            if (!seen.has(s.id)) { seen.add(s.id); merged.push(s); }
          }
        }
        setSignatories(merged);
      })
      .catch(() => setSignatories([]));
  }, [c?.customer_id]);

  // PDF helpers — fetch with auth header into a blob, then either open
  // or download. The browser's native PDF viewer is faster than an
  // embedded iframe and respects the user's zoom / search preferences.
  // We keep the response object around so the download path can read
  // the Content-Disposition filename the backend sets.
  const fetchPdfResponse = async () => (
    axios.get(`${API}/contracts/${contractId}/pdf`, { responseType: 'blob' })
  );
  const openPdfInNewTab = async () => {
    setPdfLoading(true);
    try {
      const r = await fetchPdfResponse();
      const url = window.URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF generation failed');
    } finally {
      setPdfLoading(false);
    }
  };
  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await fetchPdfResponse();
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      // Backend supplies the canonical name
      // (Type-Number-PartyA-PartyB-dd_mm_yyyy.pdf).
      a.download = filenameFromContentDisposition(
        r.headers['content-disposition'],
        `Document-${(c?.contract_number || contractId).replace(/\//g, '-')}.pdf`,
      );
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF generation failed');
    } finally {
      setPdfLoading(false);
    }
  };

  const save = async ({ silent = false } = {}) => {
    try {
      await axios.put(`${API}/contracts/${contractId}`, {
        template_id: c.template_id,
        title: c.title, employee_id: c.employee_id, customer_id: c.customer_id,
        project_id: c.project_id, variables: c.variables || {},
        sign_date: c.sign_date, valid_from: c.valid_from, valid_to: c.valid_to,
        place_of_signing: c.place_of_signing,
        signatory_id: c.signatory_a_id || c.signatory_id || null,
        signatory_a_id: c.signatory_a_id || null,
        signatory_b_id: c.signatory_b_id || null,
        excluded_paragraph_ids: c.excluded_paragraph_ids || [],
        contract_number: c.contract_number,
      });
      if (!silent) {
        toast.success(t('auto.ctr.saved'));
        await reload();
      }
    } catch (e) {
      if (silent) throw e;
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  // Autosave — draft contracts only (signed contracts are immutable).
  const autosaveState = useAutosave(
    c ? {
      title: c.title, employee_id: c.employee_id, customer_id: c.customer_id,
      project_id: c.project_id, variables: c.variables, sign_date: c.sign_date,
      valid_from: c.valid_from, valid_to: c.valid_to, place_of_signing: c.place_of_signing,
      signatory_a_id: c.signatory_a_id, signatory_b_id: c.signatory_b_id,
      excluded_paragraph_ids: c.excluded_paragraph_ids, contract_number: c.contract_number,
    } : null,
    () => save({ silent: true }),
    { enabled: !!c && c.status !== 'signed' },
  );

  const sign = async () => {
    if (!window.confirm(t('auto.ctr.signAndLockThisContractAfter'))) return;
    try {
      await axios.post(`${API}/contracts/${contractId}/sign?role=Ordering+Party`);
      toast.success(t('auto.ctr.signed'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sign failed'); }
  };

  const toggleParagraphExcluded = (paraId) => {
    const excluded = new Set(c.excluded_paragraph_ids || []);
    if (excluded.has(paraId)) excluded.delete(paraId); else excluded.add(paraId);
    setC(prev => ({ ...prev, excluded_paragraph_ids: Array.from(excluded)  }));
  };
  const isExcluded = (paraId) => (c?.excluded_paragraph_ids || []).includes(paraId);

  const optionalParagraphs = useMemo(() => {
    const list = (c?.paragraphs_preview || c?.paragraphs_resolved || []);
    return list.filter((p) => p.optional);
  }, [c]);

  const allowsSignatory = c?.kind === 'support_letter' || c?.kind === 'client_letter';  // eslint-disable-line no-unused-vars

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="contract-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {c?.contract_number} · {c?.title}
            <Badge className={`${STATUS_BADGE[c?.status] || ''} text-[10px]`}>{(c?.status || '').replace(/_/g, ' ')}</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Edit contract metadata, optional paragraphs and the customer
            stamp signatory. Use <strong>{t('auto.ctr.openPdfInNewTab')}</strong> below
            to view the rendered output.
          </DialogDescription>
        </DialogHeader>

        {loading || !c ? (
          <div className="text-center py-10 text-slate-400">{t('auto.ctr.loading')}</div>
        ) : (
          <div className="space-y-4">
            {c.kind === 'annex' && <InheritancePanel contractId={c.id} />}
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('auto.ctr.metadata')}</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>{t('auto.ctr.contractNumber')}</Label>
                  <Input
                    value={c.contract_number || ''}
                    onChange={(e) => setC(prev => ({ ...prev, contract_number: e.target.value  }))}
                    onBlur={(e) => checkNumberInUse(e.target.value)}
                    disabled={c.status === 'signed'}
                    className={numberInUse ? 'border-amber-500 focus-visible:ring-amber-500' : ''}
                    data-testid="contract-number-input"
                  />
                  {numberInUse && numberInUse.length > 0 && (
                    <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                         data-testid="contract-number-duplicate-warning">
                      ⚠ This number is already in use by{' '}
                      {numberInUse.slice(0, 3).map((m, i) => (
                        <span key={m.id}>
                          <span className="font-mono">{m.contract_number}</span>
                          {m.title ? ` (${m.title})` : ''}
                          {i < Math.min(2, numberInUse.length - 1) ? ', ' : ''}
                        </span>
                      ))}
                      {numberInUse.length > 3 ? ` and ${numberInUse.length - 3} more` : ''}
                      . Saving anyway will create a duplicate.
                    </div>
                  )}
                </div>
                <div>
                  <Label>{t('auto.ctr.title')}</Label>
                  <Input value={c.title || ''} onChange={(e) => setC(prev => ({ ...prev, title: e.target.value  }))}
                    disabled={c.status === 'signed'} />
                </div>
                <div>
                  <Label>{t('auto.ctr.signDate')}</Label>
                  <Input type="date" value={c.sign_date || ''} onChange={(e) => setC(prev => ({ ...prev, sign_date: e.target.value  }))}
                    disabled={c.status === 'signed'} />
                </div>
                <div>
                  <Label>{t('auto.ctr.placeOfSigning')}</Label>
                  <Input value={c.place_of_signing || ''} onChange={(e) => setC(prev => ({ ...prev, place_of_signing: e.target.value  }))}
                    disabled={c.status === 'signed'} />
                </div>
                <div>
                  <Label>{t('auto.ctr.project')}</Label>
                  <Select value={c.project_id || 'none'}
                    onValueChange={(v) => setC(prev => ({ ...prev, project_id: v === 'none' ? null : v  }))}>
                    <SelectTrigger disabled={c.status === 'signed'}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('auto.ctr.validFrom')}</Label>
                  <Input type="date" value={c.valid_from || ''} onChange={(e) => setC(prev => ({ ...prev, valid_from: e.target.value  }))}
                    disabled={c.status === 'signed'} />
                </div>
                <div>
                  <Label>{t('auto.ctr.validTo')}</Label>
                  <Input type="date" value={c.valid_to || ''} onChange={(e) => setC(prev => ({ ...prev, valid_to: e.target.value  }))}
                    disabled={c.status === 'signed'} />
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['party_a', 'party_b'].map((p) => (
                <Card key={p}>
                  <CardHeader><CardTitle className="text-xs uppercase text-slate-500">{p.replace('_', ' ')}</CardTitle></CardHeader>
                  <CardContent className="text-xs space-y-0.5">
                    <div className="font-semibold">{c[p]?.name || '—'}</div>
                    {c[p]?.legal_form && <div className="text-slate-500">{c[p].legal_form}</div>}
                    <div>{[c[p]?.address, c[p]?.postal_code, c[p]?.city].filter(Boolean).join(', ')}</div>
                    {c[p]?.country && <div>{c[p].country}</div>}
                    {c[p]?.nip && <div>NIP: {c[p].nip}</div>}
                    {c[p]?.pesel && <div>PESEL: {c[p].pesel}</div>}
                    {c[p]?.email && <div>{c[p].email}</div>}
                    {c[p]?.representative_name && (
                      <div className="italic">Rep: {c[p].representative_name} — {c[p].representative_position}</div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Tag className="h-4 w-4" /> {t('auto.ctr.customVariablesInput')}</CardTitle></CardHeader>
              <CardContent>
                <VariableEditor value={c.variables || {}} disabled={c.status === 'signed'}
                  onChange={(v) => setC(prev => ({ ...prev, variables: v  }))} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-2">
                <FileSignature className="h-4 w-4" /> {t('auto.ctr.signatories')}
              </CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { field: 'signatory_a_id', label: `Party A — ${c.party_a?.name || 'signatory'}`, ns: 'signatory_a' },
                  { field: 'signatory_b_id', label: `Party B — ${c.party_b?.name || 'signatory'}`, ns: 'signatory_b' },
                ].map(({ field, label, ns }) => (
                  <div key={field} className="space-y-2">
                    <Label className="text-xs">{label}</Label>
                    <Select value={c[field] || 'none'}
                      onValueChange={(v) => setC(prev => ({ ...prev, [field]: v === 'none' ? null : v }))}>
                      <SelectTrigger disabled={c.status === 'signed'} data-testid={`contract-edit-${ns}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('auto.ctr.none')}</SelectItem>
                        {signatories.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}{s.role_label ? ` (${s.role_label})` : ''}
                            {!s.customer_id ? ' · Expertfix' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {c[field] && <SignatoryStampPreview signatoryId={c[field]} />}
                    <p className="text-[10px] text-slate-400 font-mono">
                      {`{{${ns}.name}}`} · {`{{${ns}.signature}}`}
                    </p>
                  </div>
                ))}
                {!signatories.length && (
                  <p className="sm:col-span-2 text-xs text-amber-600">
                    No signatories available. Add one under <em>Stamps &amp; Signatories</em>.
                  </p>
                )}
              </CardContent>
            </Card>

            {optionalParagraphs.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm">{t('auto.ctr.optionalParagraphs')}</CardTitle></CardHeader>
                <CardContent className="space-y-1.5">
                  <p className="text-xs text-slate-500 mb-2">
                    Uncheck a paragraph to exclude it from this {c.kind?.replace('_', ' ') || 'contract'}.
                  </p>
                  {optionalParagraphs.map((p, pIdx) => {
                    const body = p.body || '';
                    const imgMatches = [...body.matchAll(/<img[^>]+src="([^"]+)"[^>]*>/gi)]
                      .map((m) => m[1])
                      .slice(0, 4);
                    const stripped = body
                      .replace(/<img[^>]*>/gi, ' [image] ')
                      .replace(/<[^>]+>/g, '')
                      .replace(/\s+/g, ' ')
                      .trim();
                    const headingLine = p.is_preamble
                      ? (p.heading || (stripped ? stripped.slice(0, 80) : '(unnumbered)'))
                      : `§ ${p.heading || '…'}`;
                    return (
                      <label key={p.id || `opt-${pIdx}`} className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1 shrink-0"
                          disabled={c.status === 'signed'}
                          checked={!isExcluded(p.id)}
                          onChange={() => toggleParagraphExcluded(p.id)}
                          data-testid={`optional-toggle-${p.id}`}
                        />
                        <span className="min-w-0 flex-1 break-words">
                          <span className="font-medium break-words">{headingLine}</span>
                          {stripped && (
                            <span className="block text-xs text-slate-500 line-clamp-2 break-words">
                              {stripped}
                            </span>
                          )}
                          {imgMatches.length > 0 && (
                            <span className="flex flex-wrap gap-2 mt-1">
                              {imgMatches.map((src, i) => (
                                <img key={i} src={src} alt="" className="h-10 max-w-[120px] object-contain border bg-slate-50 p-0.5" />
                              ))}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle className="text-sm">{t('auto.ctr.pdf')}</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <Button
                    size="sm"
                    onClick={openPdfInNewTab}
                    disabled={pdfLoading}
                    data-testid="contract-open-pdf"
                  >
                    {pdfLoading ? t('auto.ctr.generating') : t('auto.ctr.openPdfInNewTab')}
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => downloadPdf()}
                    disabled={pdfLoading}
                    data-testid="contract-download-pdf"
                  >
                    {t('auto.ctr.download')}
                  </Button>
                  <span className="text-xs text-slate-500">
                    Save your changes first — the PDF reflects the last save.
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter className="gap-2">
          <span className="self-center text-xs text-slate-500 mr-auto" data-testid="contract-autosave-indicator">
            {AUTOSAVE_LABEL[autosaveState]}
          </span>
          <Button variant="outline" onClick={onClose} data-testid="dialog-close-btn">{t('auto.ctr.close')}</Button>
          {c?.kind === 'master' && c?.status === 'signed' && (
            <Button variant="outline" onClick={() => setAnnexOpen(true)} data-testid="contract-new-annex">
              <Plus className="h-4 w-4 mr-1" /> {t('auto.ctr.newAnnex')}
            </Button>
          )}
          {c?.status !== 'signed' && (
            <>
              <Button variant="outline" onClick={() => save()} data-testid="contract-save">{t('auto.ctr.saveDraft')}</Button>
              <Button onClick={sign} className="bg-green-600 hover:bg-green-700" data-testid="contract-sign">
                <Lock className="h-4 w-4 mr-1" /> Sign &amp; lock
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      {annexOpen && c && (
        <AnnexCreateDialog
          masterContract={c}
          open={annexOpen}
          onClose={() => setAnnexOpen(false)}
          onSaved={() => { setAnnexOpen(false); onSaved(); }}
          templates={templates.filter((t) => t.kind === 'annex')}
          projects={projects}
        />
      )}
    </Dialog>
  );
}
